/**
 * PhaseAccordion — collapsible card per workflow phase.
 *
 * Header shows phase name, status pill, completion ratio, and day window.
 * Expanded body lists every StepRow that belongs to that phase.
 */
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import StepRow from './StepRow';
import { stepsInPhase, phaseProgress, phaseStatus, findCurrentStep } from '../fixtures';

const STATUS_LABEL = {
  pending:     'Not started',
  in_progress: 'In progress',
  blocked:     'Blocked',
  done:        'Complete',
};

const PhaseAccordion = ({ phase, order, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const states = order.stepStates || {};
  const status = phaseStatus(phase, states);
  const { done, total } = phaseProgress(phase, states);
  const steps = stepsInPhase(phase.id);
  const currentStep = findCurrentStep(states);

  const pct = Math.round((done / total) * 100);

  return (
    <div className={`manifest-phase-card status-${status} phase-${phase.id} ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="manifest-phase-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className="manifest-phase-num">{phase.id}</div>
        <div className="manifest-phase-headline">
          <div className="manifest-phase-title">
            {phase.label}
            <span className={`manifest-phase-status status-${status}`}>{STATUS_LABEL[status]}</span>
          </div>
          <div className="manifest-phase-sub">
            <span>{phase.dayWindow}</span>
            <span className="manifest-phase-dot" />
            <span>{phase.actor}</span>
            <span className="manifest-phase-dot" />
            <span>{done} / {total} steps</span>
          </div>
        </div>
        <div className="manifest-phase-progress">
          <div className="manifest-phase-progress-bar">
            <div className="manifest-phase-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="manifest-phase-progress-pct">{pct}%</span>
        </div>
        <ChevronDown size={18} className={`manifest-phase-chevron ${open ? 'open' : ''}`} />
      </button>

      {open && (
        <div className="manifest-phase-body">
          {steps.map((s) => (
            <StepRow
              key={s.id}
              step={s}
              defaultExpanded={s.id === currentStep.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PhaseAccordion;
